import { Button, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { number, object, string } from 'yup';
import { useCreateLayout } from '~/api/layouts';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent, DialogForm } from '~/components/dialogs/lib/styles';
import { NumberPairField } from '~/components/forms/number-pair';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { i18n } from '~/lib/i18n';

export interface NewLayoutDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  open: boolean;
}

export const NewLayoutDialog = ({ close, open }: NewLayoutDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [createLayout] = useCreateLayout();
  const navigate = useNavigate();
  const link = useLink();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: '',
      height: 1080,
      name: '',
      width: 1920,
    },
    onSubmit: async (values) => {
      const { data, errors } = await createLayout({
        variables: { input: { ...values, networkId: currentNetwork.id } },
      });
      if (!errors?.length) {
        close();
        navigate(link(`/layouts/${data?.createLayout?.layout?.id}`));
      }
    },
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      description: string().optional().label(i18n.t('common.labels.description')),
      height: number()
        .required(i18n.t('pages.layouts.new_dialog.validation.height_required'))
        .min(0, i18n.t('pages.layouts.new_dialog.validation.height_min'))
        .label(i18n.t('common.labels.height'))
        .transform((currentValue) => {
          return isNaN(parseFloat(currentValue)) ? undefined : parseFloat(currentValue);
        }),
      name: string()
        .required(i18n.t('common.validation.name_required'))
        .label(i18n.t('common.labels.name')),
      width: number()
        .required(i18n.t('pages.layouts.new_dialog.validation.width_required'))
        .min(0, i18n.t('pages.layouts.new_dialog.validation.width_min'))
        .label(i18n.t('common.labels.width'))
        .transform((currentValue) => {
          return isNaN(parseFloat(currentValue)) ? undefined : parseFloat(currentValue);
        }),
    }),
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={close}>{t('pages.layouts.new_dialog.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.layouts.new_dialog.description')}</DialogContentTitle>

        <DialogForm onSubmit={formik.handleSubmit} id="new-layout-form">
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              focused
              autoFocus
            />
          </label>
          <label>
            <span className={`label`}>{t('common.labels.description')}</span>
            <TextField
              fullWidth
              multiline
              minRows={2}
              name="description"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.description}
            />
          </label>
          <NumberPairField
            fields={[
              { label: t('common.labels.width'), name: 'width', sx: { mr: 1 } },
              { label: t('common.labels.height'), name: 'height', sx: { ml: 1 } },
            ]}
            formik={formik}
            label={t('common.labels.size')}
            required
            separator={<span>x</span>}
          />
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.actions.cancel')}
        </Button>
        <Button
          color="primary"
          form="new-layout-form"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          type="submit"
          variant="contained"
        >
          {t('pages.layouts.new_dialog.actions.create_layout')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
